import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, FileText, RefreshCw, CreditCard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Bills() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [newBill, setNewBill] = useState({
    vendor_id: '', bill_number: '', bill_date: new Date().toISOString().split('T')[0], due_date: '',
    items: [{ description: '', hsn_code: '', quantity: 1, unit_price: 0 }], notes: ''
  });
  const [payment, setPayment] = useState({ amount: 0, payment_date: new Date().toISOString().split('T')[0], payment_mode: 'bank_transfer', reference_number: '' });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billRes, vendRes] = await Promise.all([
        fetch(`${API}/api/purchases/bills`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/purchases/vendors`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (billRes.ok) setBills((await billRes.json()).bills || []);
      if (vendRes.ok) setVendors((await vendRes.json()).vendors || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createBill = async () => {
    if (!newBill.vendor_id || !newBill.bill_number || !newBill.items[0].description) { toast.error('Fill required fields'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/bills`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newBill)
      });
      if (res.ok) { toast.success('Bill recorded'); setShowNew(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const recordPayment = async () => {
    if (!selectedBill || payment.amount <= 0) { toast.error('Enter valid amount'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/payments`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendor_id: selectedBill.vendor_id, bill_ids: [selectedBill.bill_id], ...payment })
      });
      if (res.ok) { toast.success('Payment recorded'); setShowPay(false); setSelectedBill(null); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Bills</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Record Bill</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Record Vendor Bill</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label>Vendor *</Label><Select value={newBill.vendor_id} onValueChange={(v) => setNewBill({ ...newBill, vendor_id: v })}><SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.company_name}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Bill Number *</Label><Input value={newBill.bill_number} onChange={(e) => setNewBill({ ...newBill, bill_number: e.target.value })} /></div>
                  <div><Label>Bill Date</Label><Input type="date" value={newBill.bill_date} onChange={(e) => setNewBill({ ...newBill, bill_date: e.target.value })} /></div>
                  <div><Label>Due Date</Label><Input type="date" value={newBill.due_date} onChange={(e) => setNewBill({ ...newBill, due_date: e.target.value })} /></div>
                </div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Line Item</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description *</Label><Input value={newBill.items[0].description} onChange={(e) => setNewBill({ ...newBill, items: [{ ...newBill.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">HSN</Label><Input value={newBill.items[0].hsn_code} onChange={(e) => setNewBill({ ...newBill, items: [{ ...newBill.items[0], hsn_code: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={newBill.items[0].quantity} onChange={(e) => setNewBill({ ...newBill, items: [{ ...newBill.items[0], quantity: parseInt(e.target.value) || 1 }] })} /></div>
                    <div><Label className="text-xs">Unit Price</Label><Input type="number" min="0" value={newBill.items[0].unit_price} onChange={(e) => setNewBill({ ...newBill, items: [{ ...newBill.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={newBill.notes} onChange={(e) => setNewBill({ ...newBill, notes: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={createBill} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Record Bill</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Bill #</TableHead><TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {bills.map((b) => (
              <TableRow key={b.bill_id}>
                <TableCell className="font-medium">{b.bill_number}</TableCell>
                <TableCell>{b.bill_date}</TableCell>
                <TableCell>{b.vendor_name}</TableCell>
                <TableCell className="text-right">{formatCurrency(b.total_amount)}</TableCell>
                <TableCell className="text-right text-green-600">{formatCurrency(b.paid_amount)}</TableCell>
                <TableCell><Badge variant="outline" className={b.payment_status === 'paid' ? 'border-green-500 text-green-600' : b.payment_status === 'partial' ? 'border-amber-500 text-amber-600' : 'border-red-500 text-red-600'}>{b.payment_status}</Badge></TableCell>
                <TableCell><Button variant="outline" size="sm" disabled={b.payment_status === 'paid'} onClick={() => { setSelectedBill(b); setPayment({ ...payment, amount: b.balance_due || (b.total_amount - (b.paid_amount || 0)) }); setShowPay(true); }}><CreditCard className="h-3 w-3 mr-1" />Pay</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {bills.length === 0 && <div className="text-center py-8 text-slate-500">No bills recorded</div>}
      </CardContent>
      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount *</Label><Input type="number" min="0" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Date</Label><Input type="date" value={payment.payment_date} onChange={(e) => setPayment({ ...payment, payment_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Mode</Label><Select value={payment.payment_mode} onValueChange={(v) => setPayment({ ...payment, payment_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="cash">Cash</SelectItem></SelectContent></Select></div>
              <div><Label>Reference</Label><Input value={payment.reference_number} onChange={(e) => setPayment({ ...payment, reference_number: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowPay(false)}>Cancel</Button><Button onClick={recordPayment} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Record Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
